import { useState, useMemo, useEffect } from "react";
import Select from 'react-select';
import AttendanceChart from "./AttendanceChart";
import StatsDateSelector from "./StatsDateSelector";
import StatsEventFilters from "./StatsEventFilters";
import moment from "moment";
import { isAssaigDeMusics, isReunio } from "../../utils/utils";
import { getPersistentItem, setPersistentItem } from "../../utils/storage";
import { isHiddenStatsRehearsal } from './statsEventCategories';
import "./StatsIndividualTab.css";

function StatsIndividualTab({
    chosenUsers,
    setChosenUsers,
    events,
    eventCategories,
    assistenciesDict,
    castellersInfo,
    passatFirst,
    setPassatFirst,
    grups,
    displayName,
    filtersEvents,
    setFiltersEvents,
    minAttendees,
    setMinAttendees,
}) {
    // Load keywordFilters from localStorage or use default
    const [keywordFilters, setKeywordFilters] = useState(() => {
        const saved = getPersistentItem('stats_individual_keywordFilters');
        if (saved) {
            try {
                return JSON.parse(saved);
            } catch (e) {
                console.error('Error parsing stats_individual_keywordFilters from localStorage:', e);
            }
        }
        return [];
    })

    // Load countFitxat from localStorage or use default
    const [countFitxat, setCountFitxat] = useState(() => {
        const saved = getPersistentItem('stats_individual_countFitxat');
        return saved === 'true';
    })

    const [start_date, setStart_date] = useState(moment().startOf('year').format('YYYY-MM-DD'))

    // Save keywordFilters to localStorage when it changes
    useEffect(() => {
        setPersistentItem('stats_individual_keywordFilters', JSON.stringify(keywordFilters));
    }, [keywordFilters]);

    // Save countFitxat to localStorage when it changes
    useEffect(() => {
        setPersistentItem('stats_individual_countFitxat', String(countFitxat));
    }, [countFitxat]);

    // Default: 31st December of this year
    const [end_date, setEnd_date] = useState(moment().endOf('year').format('YYYY-MM-DD'))

    const filteredEvents = useMemo(() => {
        return events
            .filter(e => moment(e['data-esperada-inici']).isBetween(start_date, end_date, 'day', '[]'))
            // Event type filters
            .filter(e => filtersEvents?.activitats ? e.tipus !== 'activitat' : true)
            .filter(e => filtersEvents?.actuacions ? e.tipus !== 'actuació' : true)
            .filter(e => filtersEvents?.assaigs ? (e.tipus !== 'assaig' || isAssaigDeMusics(e)) : true)
            .filter(e => !isHiddenStatsRehearsal(e, filtersEvents))
            .filter(e => filtersEvents?.comercials ? !e.title?.toLowerCase().includes('comercial') && !e.title?.toLowerCase().includes('pilar') : true)
            .filter(e => filtersEvents?.reunions ? !isReunio(e) : true)
            // Hidden events by id and title
            .filter(e => filtersEvents?.ids?.length > 0 ? !filtersEvents.ids.includes(e.id) : true)
            .filter(e => filtersEvents?.titles?.length > 0 ? !filtersEvents.titles.includes(e.title) : true)
            // Keyword filters (show only events that match at least one keyword)
            .filter(e => keywordFilters.length > 0 ? keywordFilters.some(kw => e.title?.toLowerCase().includes(kw.toLowerCase())) : true)
            .sort((ev_a, ev_b) => moment(ev_a?.['data-esperada-inici']).diff(moment(ev_b?.['data-esperada-inici'])))
    }, [events, start_date, end_date, filtersEvents, keywordFilters])

    const getListOfAttendance = user => {
        return filteredEvents
            .map(ev => {
                if (assistenciesDict?.[user]?.[ev.id]) {
                    return {
                        eventId: parseInt(ev.id),
                        status: assistenciesDict?.[user]?.[ev.id]
                    }
                } else {
                    return {
                        eventId: parseInt(ev.id),
                        status: 'No confirmat'
                    }
                }
            })
    }

    const all_users = Object.keys(castellersInfo)
        .filter(user => !filtersEvents?.canalla || castellersInfo[user]?.canalla !== 1)
        .filter(user => !filtersEvents?.music || castellersInfo[user]?.music !== 1)
        .map(user => ({
            value: user,
            label: displayName(user)
        }));

    const isGroupActive = (groupUsers) => {
        if (chosenUsers.length === 0) return false;
        const groupUserIds = groupUsers.map(u => String(u.value));
        const chosenUserIds = chosenUsers.map(u => String(u));
        
        if (chosenUserIds.length !== groupUserIds.length) return false;
        return groupUserIds.every(id => chosenUserIds.includes(id));
    };

    // Helper to check if "Tothom" is active (all users selected)
    const isTothomActive = () => {
        return isGroupActive(all_users);
    };

    return (
        <>
            <StatsDateSelector
                start_date={start_date}
                setStart_date={setStart_date}
                end_date={end_date}
                setEnd_date={setEnd_date}
            />
            
            <StatsEventFilters
                filtersEvents={filtersEvents}
                setFiltersEvents={setFiltersEvents}
                events={events}
                eventCategories={eventCategories}
                minAttendees={minAttendees}
                setMinAttendees={setMinAttendees}
                keywordFilters={keywordFilters}
                setKeywordFilters={setKeywordFilters}
                countFitxat={countFitxat}
                setCountFitxat={setCountFitxat}
                showFitxatOption={true}
                showUserTypeFilters={false}
                setChosenUsers={setChosenUsers}
                castellersInfo={castellersInfo}
            />

            <div className="stats-filters-container">
                <h2 className="stats-section-header" style={{ cursor: 'default', marginBottom: 16 }}>
                    Usuaris
                </h2>

                <div className="stats-individual-groups">
                    <div
                        className={`stats-individual-chip ${isTothomActive() ? 'active' : ''}`}
                        onClick={() => setChosenUsers(all_users.map(user => user.value))}
                    >
                        Tothom
                    </div>
                    
                    {Object.keys(grups).map(grup => (
                        <div
                            key={grup}
                            className={`stats-individual-chip ${isGroupActive(grups[grup]) ? 'active' : ''}`}
                            onClick={() => setChosenUsers(grups[grup].map(user => user.value))}
                        >
                            {grup}
                        </div>
                    ))}
                    
                    <a
                        href="/notifications/grups"
                        className="stats-individual-edit-link"
                        title="Editar grups"
                    >
                        <i className="fas fa-pencil-alt"></i>
                        Editar grups
                    </a>
                </div>

                <Select
                    styles={{
                        valueContainer: (base) => ({
                          ...base,
                          maxHeight: 200,
                          overflowY: "auto"
                        })
                    }}
                    options={all_users}
                    isMulti
                    className="basic-multi-select"
                    classNamePrefix="select"
                    value={chosenUsers.map(user => all_users.find(u => parseInt(u.value) === parseInt(user)))}
                    onChange={selectedUsers => setChosenUsers(selectedUsers.map(user => user.value))}
                    placeholder="Busca usuaris..."
                />
            </div>

            <div style={{ marginTop: 20, marginBottom: 50 }}>
                <AttendanceChart
                    attendanceDict={assistenciesDict}
                    filteredEvents={filteredEvents}
                    castellersInfo={castellersInfo}
                    chosenUsers={chosenUsers}
                    start_date={start_date}
                    end_date={end_date}
                    filtersEvents={filtersEvents}
                    setFiltersEvents={setFiltersEvents}
                    passatFirst={passatFirst}
                    setPassatFirst={setPassatFirst}
                    getListOfAttendance={getListOfAttendance}
                    countFitxat={countFitxat}
                />
            </div>
        </>
    );
}

export default StatsIndividualTab;
